import React, { useCallback, useEffect, useRef, useState } from "react"
import Button from "react-bootstrap/Button"
import { GoogleMap, useLoadScript } from "@react-google-maps/api"
import {
  defaultLat,
  defaultLng,
  googleAPIKeys,
  GOOGLE_MAPS_LIBRARIES,
} from "common/utils"
import {
  findOverlappingBoundary,
  hasRingSelfIntersection,
  isPointInsideExistingZones,
  pointsToGeoJson,
  toLatLngLiteral,
  toLatLngPaths,
} from "common/geoBoundaryUtils"
import {
  createZoneMapLabelOverlayFromPaths,
  removeZoneMapLabelOverlays,
} from "common/zoneMapLabelOverlay"
import { useTranslation } from "react-i18next"

const mapContainerStyle = { width: "100%", height: "480px" }

// Must stay referentially stable: GoogleMap calls setCenter whenever the
// center prop changes, which would snap the map back on every re-render.
const DEFAULT_CENTER = { lat: defaultLat, lng: defaultLng }
const MAP_OPTIONS = {
  clickableIcons: false,
  streetViewControl: false,
  fullscreenControl: true,
}
const DRAW_COLOR = "#1976d2"

const pathToGeoJson = path => {
  const coords = path
    .getArray()
    .map(latLng => {
      const literal = toLatLngLiteral(latLng)
      return literal ? [literal.lng, literal.lat] : null
    })
    .filter(Boolean)
  if (coords.length < 3) return null

  const first = coords[0]
  const last = coords[coords.length - 1]
  if (first[0] !== last[0] || first[1] !== last[1]) {
    coords.push([...first])
  }

  return { type: "Polygon", coordinates: [coords] }
}

const isSamePoint = (pointA, pointB) =>
  Boolean(pointA && pointB) &&
  Math.abs(pointA.lat - pointB.lat) < 1e-9 &&
  Math.abs(pointA.lng - pointB.lng) < 1e-9

const fitMapToPaths = (map, paths) => {
  const bounds = new window.google.maps.LatLngBounds()
  let hasPoints = false
  paths.forEach(point => {
    const literal = toLatLngLiteral(point)
    if (literal) {
      bounds.extend(literal)
      hasPoints = true
    }
  })
  if (hasPoints) map.fitBounds(bounds)
}

const createVertexMarker = (map, position, isFirst) =>
  new window.google.maps.Marker({
    map,
    position,
    clickable: isFirst,
    cursor: isFirst ? "pointer" : undefined,
    zIndex: isFirst ? 3 : 2,
    icon: {
      path: window.google.maps.SymbolPath.CIRCLE,
      scale: isFirst ? 7 : 5,
      fillColor: isFirst ? "#ffffff" : DRAW_COLOR,
      fillOpacity: 1,
      strokeColor: DRAW_COLOR,
      strokeWeight: 2,
    },
  })

const isTypingTarget = target =>
  target?.isContentEditable ||
  ["INPUT", "TEXTAREA", "SELECT"].includes(target?.tagName)

const RegionBoundaryPicker = ({
  boundary,
  onBoundaryChange,
  existingBoundaries = [],
  excludeZoneId = null,
  zonesReady = true,
  currentZoneLabel = "",
}) => {
  const { t, i18n } = useTranslation()
  const mapRef = useRef(null)
  const polygonRef = useRef(null)
  const polygonRightClickListenerRef = useRef(null)
  const draftPolygonRef = useRef(null)
  const draftLineRef = useRef(null)
  const guideLineRef = useRef(null)
  const vertexMarkersRef = useRef([])
  const drawingListenersRef = useRef([])
  const pathListenersRef = useRef([])
  const existingPolygonsRef = useRef([])
  const existingZoneLabelsRef = useRef([])
  const currentZoneLabelOverlayRef = useRef(null)
  const skipExternalSyncRef = useRef(false)
  const hasFittedExistingRef = useRef(false)
  const drawingPointsRef = useRef([])
  const lastValidBoundaryRef = useRef(boundary)
  const existingBoundariesRef = useRef(existingBoundaries)
  const excludeZoneIdRef = useRef(excludeZoneId)
  const [isDrawing, setIsDrawing] = useState(false)
  const [pointCount, setPointCount] = useState(0)
  const [overlapError, setOverlapError] = useState(null)

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: googleAPIKeys,
    libraries: GOOGLE_MAPS_LIBRARIES,
  })

  existingBoundariesRef.current = existingBoundaries
  excludeZoneIdRef.current = excludeZoneId

  const clearPathListeners = () => {
    pathListenersRef.current.forEach(listener =>
      window.google.maps.event.removeListener(listener)
    )
    pathListenersRef.current = []
  }

  const removePolygon = () => {
    if (polygonRightClickListenerRef.current) {
      window.google.maps.event.removeListener(
        polygonRightClickListenerRef.current
      )
      polygonRightClickListenerRef.current = null
    }
    if (polygonRef.current) {
      polygonRef.current.setMap(null)
      polygonRef.current = null
    }
    clearPathListeners()
  }

  const emitBoundary = useCallback(
    geoJson => {
      skipExternalSyncRef.current = true
      onBoundaryChange(geoJson)
    },
    [onBoundaryChange]
  )

  const getZoneLabel = useCallback(
    zone =>
      i18n.language === "sa"
        ? zone?.nameAr || zone?.name
        : zone?.name || zone?.nameAr,
    [i18n.language]
  )

  const getZoneLabelRef = useRef(getZoneLabel)
  getZoneLabelRef.current = getZoneLabel
  const currentZoneLabelRef = useRef(currentZoneLabel)
  currentZoneLabelRef.current = currentZoneLabel

  const clearExistingPolygons = useCallback(() => {
    existingPolygonsRef.current.forEach(polygon => polygon.setMap(null))
    existingPolygonsRef.current = []
    removeZoneMapLabelOverlays(existingZoneLabelsRef.current)
    existingZoneLabelsRef.current = []
  }, [])

  const clearCurrentZoneLabel = useCallback(() => {
    if (currentZoneLabelOverlayRef.current) {
      currentZoneLabelOverlayRef.current.setMap(null)
      currentZoneLabelOverlayRef.current = null
    }
  }, [])

  const updateCurrentZoneLabel = useCallback(
    (map, geoJson, label) => {
      clearCurrentZoneLabel()
      if (!map || !geoJson?.coordinates?.[0]?.length) return

      const paths = toLatLngPaths(geoJson.coordinates[0])
      if (paths.length < 3) return
      const labelOverlay = createZoneMapLabelOverlayFromPaths(
        map,
        paths,
        label,
        DRAW_COLOR
      )
      if (labelOverlay) {
        currentZoneLabelOverlayRef.current = labelOverlay
      }
    },
    [clearCurrentZoneLabel]
  )

  const showExistingBoundaries = useCallback(
    map => {
      clearExistingPolygons()
      if (!map) return

      const visiblePaths = []
      existingBoundaries.forEach(zone => {
        if (excludeZoneId && String(zone._id) === String(excludeZoneId)) {
          return
        }
        if (!zone.boundary?.coordinates?.[0]?.length) return

        const paths = toLatLngPaths(zone.boundary.coordinates[0])
        if (paths.length < 3) return
        visiblePaths.push(...paths)
        const polygon = new window.google.maps.Polygon({
          paths,
          editable: false,
          draggable: false,
          clickable: false,
          map,
          strokeColor: "#d32f2f",
          strokeOpacity: 0.85,
          strokeWeight: 2,
          fillColor: "#d32f2f",
          fillOpacity: 0.12,
        })
        existingPolygonsRef.current.push(polygon)

        const labelOverlay = createZoneMapLabelOverlayFromPaths(
          map,
          paths,
          getZoneLabel(zone),
          "#d32f2f"
        )
        if (labelOverlay) {
          existingZoneLabelsRef.current.push(labelOverlay)
        }
      })

      // Without a boundary of its own, open the map on the existing zones
      // (once) instead of the country-wide default.
      if (
        !hasFittedExistingRef.current &&
        visiblePaths.length &&
        !lastValidBoundaryRef.current?.coordinates?.[0]?.length &&
        !drawingPointsRef.current.length
      ) {
        hasFittedExistingRef.current = true
        fitMapToPaths(map, visiblePaths)
      }
    },
    [clearExistingPolygons, existingBoundaries, excludeZoneId, getZoneLabel]
  )

  const detectOverlap = useCallback(geoJson => {
    return findOverlappingBoundary(
      geoJson,
      existingBoundariesRef.current,
      excludeZoneIdRef.current
    )
  }, [])

  const tryEmitBoundaryRef = useRef(() => true)

  const showPolygonWithListeners = useCallback(
    (map, geoJson, { label = currentZoneLabelRef.current, fit = true } = {}) => {
      if (!geoJson?.coordinates?.[0]?.length) return

      removePolygon()

      const paths = toLatLngPaths(geoJson.coordinates[0])
      if (paths.length < 3) return
      // GeoJSON repeats the first point to close the ring; Google closes it itself.
      if (paths.length > 3 && isSamePoint(paths[0], paths[paths.length - 1])) {
        paths.pop()
      }

      polygonRef.current = new window.google.maps.Polygon({
        paths,
        editable: true,
        draggable: false,
        map,
        strokeColor: DRAW_COLOR,
        strokeOpacity: 0.9,
        strokeWeight: 2,
        fillColor: DRAW_COLOR,
        fillOpacity: 0.2,
      })

      const path = polygonRef.current.getPath()
      pathListenersRef.current = ["set_at", "insert_at", "remove_at"].map(event =>
        window.google.maps.event.addListener(path, event, () =>
          tryEmitBoundaryRef.current(pathToGeoJson(path))
        )
      )
      // Right-click a vertex to delete it (a polygon keeps at least 3).
      polygonRightClickListenerRef.current = polygonRef.current.addListener(
        "rightclick",
        event => {
          if (event.vertex == null || path.getLength() <= 3) return
          path.removeAt(event.vertex)
        }
      )

      updateCurrentZoneLabel(map, geoJson, label)

      if (fit) fitMapToPaths(map, paths)
    },
    [updateCurrentZoneLabel]
  )

  const revertToLastValidBoundary = useCallback(() => {
    const map = mapRef.current
    if (!map) return

    if (lastValidBoundaryRef.current?.coordinates?.[0]?.length) {
      showPolygonWithListeners(map, lastValidBoundaryRef.current, { fit: false })
      return
    }

    removePolygon()
  }, [showPolygonWithListeners])

  const tryEmitBoundary = useCallback(
    geoJson => {
      if (!geoJson) {
        setOverlapError(null)
        lastValidBoundaryRef.current = null
        clearCurrentZoneLabel()
        emitBoundary(null)
        return true
      }

      if (hasRingSelfIntersection(geoJson.coordinates[0])) {
        setOverlapError(
          t(
            "Delivery boundary is invalid because polygon edges cross. Redraw the boundary without overlapping lines."
          )
        )
        revertToLastValidBoundary()
        return false
      }

      const overlappingZone = detectOverlap(geoJson)

      if (overlappingZone) {
        setOverlapError(
          t(
            'This boundary overlaps with zone "{{name}}". Adjust it so zones do not overlap.',
            { name: getZoneLabel(overlappingZone) }
          )
        )
        revertToLastValidBoundary()
        return false
      }

      setOverlapError(null)
      lastValidBoundaryRef.current = geoJson
      emitBoundary(geoJson)
      if (mapRef.current) {
        updateCurrentZoneLabel(
          mapRef.current,
          geoJson,
          currentZoneLabelRef.current
        )
      }
      return true
    },
    [
      clearCurrentZoneLabel,
      detectOverlap,
      emitBoundary,
      getZoneLabel,
      revertToLastValidBoundary,
      t,
      updateCurrentZoneLabel,
    ]
  )

  tryEmitBoundaryRef.current = tryEmitBoundary

  const clearVertexMarkers = () => {
    vertexMarkersRef.current.forEach(marker => marker.setMap(null))
    vertexMarkersRef.current = []
  }

  const updateDraftShapes = useCallback(cursorPoint => {
    const points = drawingPointsRef.current
    draftLineRef.current?.setPath(points)

    const previewPoints =
      cursorPoint && points.length ? [...points, cursorPoint] : points
    draftPolygonRef.current?.setPaths(
      previewPoints.length >= 3 ? previewPoints : []
    )

    if (!guideLineRef.current) return
    if (!cursorPoint || !points.length) {
      guideLineRef.current.setPath([])
      return
    }
    const lastPoint = points[points.length - 1]
    guideLineRef.current.setPath(
      points.length >= 2
        ? [lastPoint, cursorPoint, points[0]]
        : [lastPoint, cursorPoint]
    )
  }, [])

  const clearDrawingOverlays = useCallback(() => {
    drawingListenersRef.current.forEach(listener =>
      window.google.maps.event.removeListener(listener)
    )
    drawingListenersRef.current = []
    ;[draftLineRef, draftPolygonRef, guideLineRef].forEach(ref => {
      if (ref.current) {
        ref.current.setMap(null)
        ref.current = null
      }
    })
    clearVertexMarkers()
    drawingPointsRef.current = []
    setPointCount(0)
  }, [])

  const stopDrawing = useCallback(() => {
    setIsDrawing(false)
    clearDrawingOverlays()
    mapRef.current?.setOptions({
      draggableCursor: null,
      disableDoubleClickZoom: false,
    })
  }, [clearDrawingOverlays])

  const validateDrawingPoint = useCallback(
    (latLng, nextPoints) => {
      if (!zonesReady) {
        return {
          valid: false,
          message: t("Loading existing zones. Please wait before drawing."),
        }
      }

      const insideZone = isPointInsideExistingZones(
        latLng,
        existingBoundariesRef.current,
        excludeZoneIdRef.current
      )
      if (insideZone) {
        return {
          valid: false,
          message: t(
            'Cannot place points inside zone "{{name}}". Draw outside existing boundaries.',
            { name: getZoneLabelRef.current(insideZone) }
          ),
        }
      }

      if (nextPoints.length >= 3) {
        const tentativeBoundary = pointsToGeoJson(nextPoints)
        const overlappingZone = findOverlappingBoundary(
          tentativeBoundary,
          existingBoundariesRef.current,
          excludeZoneIdRef.current
        )
        if (overlappingZone) {
          return {
            valid: false,
            message: t(
              'This shape overlaps zone "{{name}}". Choose a different area.',
              { name: getZoneLabelRef.current(overlappingZone) }
            ),
          }
        }
      }

      return { valid: true }
    },
    [t, zonesReady]
  )

  const finishDrawing = useCallback(() => {
    const map = mapRef.current
    const points = drawingPointsRef.current
    if (!map || points.length < 3) return

    const geoJson = pointsToGeoJson(points)

    if (hasRingSelfIntersection(geoJson.coordinates[0])) {
      setOverlapError(
        t(
          "Delivery boundary is invalid because polygon edges cross. Redraw the boundary without overlapping lines."
        )
      )
      return
    }

    const overlappingZone = detectOverlap(geoJson)
    if (overlappingZone) {
      setOverlapError(
        t(
          'This boundary overlaps with zone "{{name}}". Adjust it so zones do not overlap.',
          { name: getZoneLabel(overlappingZone) }
        )
      )
      return
    }

    stopDrawing()
    setOverlapError(null)
    lastValidBoundaryRef.current = geoJson
    emitBoundary(geoJson)
    // The user is already looking at the shape; keep the current view.
    showPolygonWithListeners(map, geoJson, { fit: false })
  }, [
    detectOverlap,
    emitBoundary,
    getZoneLabel,
    showPolygonWithListeners,
    stopDrawing,
    t,
  ])

  const finishDrawingRef = useRef(finishDrawing)
  finishDrawingRef.current = finishDrawing

  const addDrawingPoint = useCallback(
    latLng => {
      const point = { lat: latLng.lat(), lng: latLng.lng() }
      const points = drawingPointsRef.current
      // A double-click fires two clicks on the same spot before "dblclick".
      if (isSamePoint(points[points.length - 1], point)) return

      const nextPoints = [...points, point]
      const validation = validateDrawingPoint(latLng, nextPoints)
      if (!validation.valid) {
        setOverlapError(validation.message)
        return
      }

      setOverlapError(null)
      drawingPointsRef.current = nextPoints
      setPointCount(nextPoints.length)

      const marker = createVertexMarker(
        mapRef.current,
        point,
        nextPoints.length === 1
      )
      if (nextPoints.length === 1) {
        // Clicking the first point closes the shape.
        drawingListenersRef.current.push(
          marker.addListener("click", () => finishDrawingRef.current())
        )
      }
      vertexMarkersRef.current.push(marker)
      updateDraftShapes(point)
    },
    [updateDraftShapes, validateDrawingPoint]
  )

  const undoLastPoint = useCallback(() => {
    const points = drawingPointsRef.current
    if (!points.length) return

    drawingPointsRef.current = points.slice(0, -1)
    setPointCount(drawingPointsRef.current.length)
    const marker = vertexMarkersRef.current.pop()
    if (marker) marker.setMap(null)
    setOverlapError(null)
    updateDraftShapes(null)
  }, [updateDraftShapes])

  const startDrawing = useCallback(() => {
    const map = mapRef.current
    if (!map || !zonesReady) return

    // Hide the current boundary but keep it as the value until the new shape
    // is finished, so cancelling a redraw restores it.
    removePolygon()
    clearCurrentZoneLabel()
    clearDrawingOverlays()
    setOverlapError(null)
    setIsDrawing(true)

    map.setOptions({ draggableCursor: "crosshair", disableDoubleClickZoom: true })

    draftPolygonRef.current = new window.google.maps.Polygon({
      map,
      paths: [],
      clickable: false,
      strokeOpacity: 0,
      fillColor: DRAW_COLOR,
      fillOpacity: 0.12,
    })
    draftLineRef.current = new window.google.maps.Polyline({
      map,
      path: [],
      clickable: false,
      strokeColor: DRAW_COLOR,
      strokeOpacity: 0.9,
      strokeWeight: 2,
    })
    guideLineRef.current = new window.google.maps.Polyline({
      map,
      path: [],
      clickable: false,
      strokeOpacity: 0,
      icons: [
        {
          icon: {
            path: "M 0,-1 0,1",
            strokeColor: DRAW_COLOR,
            strokeOpacity: 0.8,
            scale: 2,
          },
          offset: "0",
          repeat: "10px",
        },
      ],
    })

    drawingListenersRef.current = [
      map.addListener("click", event => addDrawingPoint(event.latLng)),
      map.addListener("mousemove", event =>
        updateDraftShapes({ lat: event.latLng.lat(), lng: event.latLng.lng() })
      ),
      map.addListener("mouseout", () => updateDraftShapes(null)),
      map.addListener("dblclick", () => finishDrawingRef.current()),
    ]
  }, [
    addDrawingPoint,
    clearCurrentZoneLabel,
    clearDrawingOverlays,
    updateDraftShapes,
    zonesReady,
  ])

  const cancelDrawing = useCallback(() => {
    stopDrawing()
    setOverlapError(null)
    revertToLastValidBoundary()
  }, [revertToLastValidBoundary, stopDrawing])

  useEffect(() => {
    if (!isDrawing) return undefined

    const handleKeyDown = event => {
      if (isTypingTarget(event.target)) return
      if (event.key === "Escape") {
        event.preventDefault()
        cancelDrawing()
      } else if (event.key === "Enter") {
        event.preventDefault()
        finishDrawingRef.current()
      } else if (
        event.key === "Backspace" ||
        (String(event.key).toLowerCase() === "z" &&
          (event.ctrlKey || event.metaKey))
      ) {
        event.preventDefault()
        undoLastPoint()
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [cancelDrawing, isDrawing, undoLastPoint])

  const handleMapLoad = useCallback(
    map => {
      mapRef.current = map
      if (boundary?.coordinates?.[0]?.length) {
        lastValidBoundaryRef.current = boundary
        showPolygonWithListeners(map, boundary)
      }
      showExistingBoundaries(map)
    },
    [boundary, showExistingBoundaries, showPolygonWithListeners]
  )

  useEffect(() => {
    if (!mapRef.current || skipExternalSyncRef.current) {
      skipExternalSyncRef.current = false
      return
    }
    if (boundary?.coordinates?.[0]?.length) {
      lastValidBoundaryRef.current = boundary
      showPolygonWithListeners(mapRef.current, boundary)
    }
  }, [boundary, showPolygonWithListeners])

  useEffect(() => {
    if (mapRef.current && lastValidBoundaryRef.current?.coordinates?.[0]?.length) {
      updateCurrentZoneLabel(
        mapRef.current,
        lastValidBoundaryRef.current,
        currentZoneLabel
      )
    }
  }, [currentZoneLabel, updateCurrentZoneLabel])

  useEffect(() => {
    if (mapRef.current) {
      showExistingBoundaries(mapRef.current)
    }
  }, [showExistingBoundaries])

  useEffect(() => {
    if (!zonesReady || !boundary?.coordinates?.[0]?.length) return

    const overlappingZone = detectOverlap(boundary)
    if (!overlappingZone) return

    setOverlapError(
      t(
        'This boundary overlaps with zone "{{name}}". Adjust it so zones do not overlap.',
        { name: getZoneLabel(overlappingZone) }
      )
    )
    lastValidBoundaryRef.current = null

    removePolygon()
    clearCurrentZoneLabel()
    emitBoundary(null)
  }, [boundary, detectOverlap, emitBoundary, getZoneLabel, t, zonesReady, clearCurrentZoneLabel])

  const handleClear = () => {
    stopDrawing()
    removePolygon()
    setOverlapError(null)
    tryEmitBoundary(null)
  }

  useEffect(() => {
    return () => {
      clearDrawingOverlays()
      clearExistingPolygons()
      clearCurrentZoneLabel()
      removePolygon()
    }
  }, [clearCurrentZoneLabel, clearDrawingOverlays, clearExistingPolygons])

  const hasBoundary = Boolean(boundary?.coordinates?.[0]?.length)

  return (
    <div>
      <p className="text-muted small mb-2">
        {t(
          "Draw the delivery boundary on the map. Existing zones are shown in red with their names and must not overlap."
        )}
      </p>
      {!zonesReady && (
        <p className="text-muted small mb-2">{t("Loading existing zones...")}</p>
      )}
      <div style={mapContainerStyle}>
        {!isLoaded ? (
          <div className="d-flex align-items-center justify-content-center h-100">
            {t("Loading")}...
          </div>
        ) : (
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={DEFAULT_CENTER}
            zoom={10}
            options={MAP_OPTIONS}
            onLoad={handleMapLoad}
          />
        )}
      </div>
      <div className="mt-2">
        {isDrawing ? (
          <>
            <Button
              variant="outline-success"
              size="sm"
              className="me-2"
              onClick={finishDrawing}
              disabled={pointCount < 3 || !zonesReady}
            >
              {t("Finish Boundary")}
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              className="me-2"
              onClick={undoLastPoint}
              disabled={!pointCount}
            >
              {t("Undo Point")}
            </Button>
            <Button variant="outline-danger" size="sm" onClick={cancelDrawing}>
              {t("Cancel")}
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="outline-primary"
              size="sm"
              className="me-2"
              onClick={startDrawing}
              disabled={!zonesReady || !isLoaded}
            >
              {hasBoundary ? t("Redraw Boundary") : t("Start Boundary")}
            </Button>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={handleClear}
              disabled={!hasBoundary}
            >
              {t("Clear Boundary")}
            </Button>
          </>
        )}
      </div>
      {isDrawing && (
        <p className="text-muted small mb-0 mt-2">
          {t(
            "Click on the map to add points. Click the first point, double-click or press Enter to finish. Backspace undoes the last point, Esc cancels."
          )}
        </p>
      )}
      {!isDrawing && hasBoundary && (
        <p className="text-muted small mb-0 mt-2">
          {t(
            "Drag the points to adjust the boundary. Drag a midpoint to add a point, right-click a point to remove it."
          )}
        </p>
      )}
      {overlapError && (
        <p className="text-danger small mb-0 mt-2">{overlapError}</p>
      )}
    </div>
  )
}

export default RegionBoundaryPicker
